/* eslint-disable sort-keys */
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import Divider from '@material-ui/core/Divider';
import { OperatingRoom, IMonitor } from '@types';
import store from '@lib/store';
import { Box } from '@atoms';
import { OperatingRoomDetails, ScreenDisplay, VitalsDisplay } from './views';
import styles from './monitor.module.scss';

export const Monitor = observer(function Monitor({
  monitor,
  cable,
}: {
  monitor?: IMonitor
  cable?: any
}) {
  const room = store.roomForMonitor(monitor);
  const facility = store.facilityForRoom(room);
  const alerts = monitor.alerts;
  const hasAlert = alerts?.length;
  const topAlert = hasAlert && alerts[0];

  // `live` tracks the room channel specifically, because that is the one
  // carrying vitals. It starts false and only the server saying "connected"
  // makes it true.
  const [live, setLive] = useState(false);

  // Subscriptions are (re)built whenever the consumer changes.
  //
  // They used to be created inside useLocalObservable's initializer, which
  // runs exactly ONCE per mount and closed over whichever consumer existed
  // then. store.reconnectCable() disposes that consumer and builds a new one —
  // on a wifi blip, a backgrounded tab, an API restart — after which these
  // subscriptions belonged to a dead socket and never received again. Nothing
  // said so: the panel kept displaying the last vitals it had received,
  // indefinitely, looking exactly like a live patient. For a monitoring
  // product that is the most dangerous failure available, and it needed only a
  // dropped connection to trigger.
  useEffect(() => {
    if (!cable || !room || !monitor) return undefined;

    const roomCable = cable.subscriptions.create(
      { channel: 'OperatingRoomChannel', id: room.id },
      {
        connected: () => setLive(true),
        disconnected: () => setLive(false),
        // The API now rejects a room outside the caller's organization. A
        // rejected subscription is permanent — it must not read as "live".
        rejected: () => setLive(false),
        received(newAttributes: OperatingRoom) {
          Object.assign(room, newAttributes);
        },
      },
    );

    const monitorCable = cable.subscriptions.create(
      { channel: 'MonitorChannel', id: monitor.id },
      {
        received(newAttributes: IMonitor) {
          Object.assign(monitor, newAttributes);
        },
      },
    );

    return () => {
      setLive(false);
      roomCable.unsubscribe();
      monitorCable.unsubscribe();
    };
    // room and monitor are MobX observables mutated in place by received()
    // above, so their identity is stable and listing them does not cause a
    // resubscribe on every vitals frame — only a genuinely different room,
    // monitor or consumer rebuilds the subscriptions.
  }, [cable, room, monitor]);

  return (
    <Box
      alignItems="center"
      bgcolor="black"
      borderRadius={4}
      className={hasAlert ? styles.alert : undefined}
      color="white"
      column
      data-monitor-id={monitor?.id}
      fullWidth
      justifyContent="center"
      mb={1}
      p={2}
      zIndex={5}
    >
      {/*
        Says out loud that these numbers are not live. Everything below renders
        the last values received, and without this a frozen panel is
        indistinguishable from a stable patient — the display looked calm and
        current when in fact nothing had arrived for minutes. Showing stale
        vitals as though they were live is worse than showing nothing.
      */}
      {!live && (
        <Box
          bgcolor="#b71c1c"
          borderRadius={4}
          color="white"
          data-testid="not-live"
          fontSize={12}
          fullWidth
          justifyContent="center"
          mb={1}
          px={1}
          py={0.5}
        >
          NOT LIVE — reconnecting, values may be out of date
        </Box>
      )}

      <OperatingRoomDetails
        monitor={monitor}
        alert={hasAlert ? topAlert : null}
        alertCount={alerts?.length ?? 0}
      />

      {/*
        Show the monitor screenshot whenever the device is on and sending —
        'active' (vitals present) OR 'standby' (the OCR read no vitals: a
        Standby / menu / blank / flat-line screen, i.e. blank_image=true). The
        API keeps frame_url populated on every update including blank ones, so
        the standby screen itself surfaces here instead of the panel vanishing.
        Only 'off' (no update in ACTIVE_TIME — device disconnected) hides it,
        since that frame would be stale; the 'Off' status + NOT LIVE cover that.
      */}
      {(room.status === 'active' || room.status === 'standby') && (
        <>
          <Divider className={styles.divider} />
          <ScreenDisplay
            facilityName={facility.name}
            frameUrl={room.frameUrl}
            roomName={room.name}
          />
          <Divider className={styles.divider} />
        </>
      )}
      {store.adminView && (
        <VitalsDisplay vitals={room.vitals} vitalSet={room.vitalSet} />
      )}
    </Box>
  );
});

export default Monitor;
